import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { connectWallet, ensureChain, getBrowserProvider, setActiveProvider } from "../services/web3.js";
import { resolveProvider } from "../services/wallets.js";
import { lookupEnsName } from "../services/ens.js";
import {
    getWalletConnectProvider,
    isWalletConnectConfigured,
    resetWalletConnectProvider
} from "../services/walletconnect.js";
import config from "../config/index.js";

const WalletContext = createContext(null);

const DISCONNECTED_FLAG_KEY = "gilder:wallet-disconnected";
const WALLET_KIND_KEY = "gilder:wallet-kind";

function readDisconnectedFlag() {
    try {
        return window.localStorage.getItem(DISCONNECTED_FLAG_KEY) === "true";
    } catch {
        return false;
    }
}

function writeDisconnectedFlag(value) {
    try {
        if (value) {
            window.localStorage.setItem(DISCONNECTED_FLAG_KEY, "true");
        } else {
            window.localStorage.removeItem(DISCONNECTED_FLAG_KEY);
        }
    } catch {
        // ignore: storage unavailable (private mode, etc.)
    }
}

function readWalletKind() {
    try {
        return window.localStorage.getItem(WALLET_KIND_KEY) || null;
    } catch {
        return null;
    }
}

function writeWalletKind(kind) {
    try {
        if (kind) window.localStorage.setItem(WALLET_KIND_KEY, kind);
        else window.localStorage.removeItem(WALLET_KIND_KEY);
    } catch {
        // ignore
    }
}

/**
 * Resolves the account list from a (possibly just-restored) WalletConnect
 * provider. The provider's own `.accounts` getter is the happy path, but
 * it can momentarily read empty right after `EthereumProvider.init()`
 * rehydrates a persisted session — so we fall back to an explicit
 * `eth_accounts` request (answered locally from the session, no prompt)
 * and finally to parsing the session's `eip155` namespace directly.
 */
async function resolveWcAccounts(wcProvider) {
    if (Array.isArray(wcProvider?.accounts) && wcProvider.accounts.length > 0) {
        return wcProvider.accounts;
    }
    try {
        const requested = await wcProvider.request({ method: "eth_accounts" });
        if (Array.isArray(requested) && requested.length > 0) return requested;
    } catch {
        // fall through to namespace parsing
    }
    try {
        // Namespace account entries look like "eip155:80002:0xABC…" —
        // the address is the third colon-separated segment.
        const raw = wcProvider?.session?.namespaces?.eip155?.accounts || [];
        const parsed = raw.map((entry) => String(entry).split(":")[2]).filter(Boolean);
        if (parsed.length > 0) return parsed;
    } catch {
        // ignore — no recoverable accounts
    }
    return [];
}

function detectKindFromProvider(provider) {
    if (!provider) return null;
    if (provider.isMetaMask) return "metamask";
    if (provider.isCoinbaseWallet || provider.isCoinbaseBrowser) return "coinbase";
    if (provider.isWalletConnect) return "walletconnect";
    return "other";
}

/**
 * Provides wallet connection state to the entire app.
 *
 * On mount, attempts a silent restore using eth_accounts (which does NOT
 * prompt the user) so a previously authorised wallet is reconnected
 * automatically after a page reload. Listens for accountsChanged and
 * chainChanged so swaps in MetaMask are reflected without a manual reload.
 */
export function WalletProvider({ children }) {
    const [address, setAddress] = useState(null);
    const [ensName, setEnsName] = useState(null);
    const [chainId, setChainId] = useState(null);
    const [signer, setSigner] = useState(null);
    const [error, setError] = useState(null);
    const [isRestoring, setIsRestoring] = useState(true);
    const [connectModalOpen, setConnectModalOpen] = useState(false);
    const [pendingDestination, setPendingDestination] = useState(null);
    const [walletKind, setWalletKind] = useState(() => readWalletKind());

    useEffect(() => {
        let cancelled = false;
        const cleanups = [];

        (async () => {
            try {
                if (readDisconnectedFlag()) return;

                const savedKind = readWalletKind();

                // If the user previously connected via WalletConnect, the
                // SDK keeps its session in its own localStorage keys.
                // Re-init the provider, and if there's still an active
                // session use it directly — no QR re-scan needed.
                //
                // "safe" is just a WalletConnect flavour (Safe is a
                // smart-contract wallet paired over WC), so it MUST take
                // the same restore path — otherwise a Safe connection is
                // dropped on every reload and the user is asked to
                // connect again even though the WC session is still live.
                const isWalletConnectKind =
                    savedKind === "walletconnect" || savedKind === "safe";
                if (isWalletConnectKind && isWalletConnectConfigured()) {
                    try {
                        const wcProvider = await getWalletConnectProvider();
                        if (cancelled) return;
                        // The persisted session rehydrates ASYNCHRONOUSLY
                        // — `EthereumProvider.init()` can resolve a beat
                        // before `.session` is actually populated (the SDK
                        // finishes restoring once its relay subscription
                        // is live). On the first post-reload mount we'd
                        // therefore read `.session` as null and force a
                        // needless reconnect, even though clicking the
                        // wallet a moment later "auto-connects" because by
                        // then the session HAS appeared. Poll briefly so
                        // the restore waits for that rehydration. We only
                        // reach this branch when the user previously
                        // connected via WC, so a short wait is warranted.
                        for (let i = 0; i < 30 && !wcProvider.session; i++) {
                            await new Promise((r) => setTimeout(r, 100));
                            if (cancelled) return;
                        }
                        // The presence of a `session` is the real "still
                        // connected" signal. The provider's `.accounts`
                        // getter, however, frequently reads EMPTY on a
                        // freshly re-inited provider (right after a page
                        // reload) until the relay finishes reconnecting —
                        // so gating on `.accounts.length` here would drop
                        // a perfectly live session and force a re-scan.
                        // We instead resolve accounts from three sources
                        // in order of reliability.
                        if (wcProvider.session) {
                            const accounts = await resolveWcAccounts(wcProvider);
                            if (cancelled) return;
                            if (accounts.length > 0) {
                                setActiveProvider(wcProvider);
                                const browserProvider = getBrowserProvider();
                                const restoredSigner = await browserProvider.getSigner();
                                if (cancelled) return;
                                setSigner(restoredSigner);
                                setAddress(accounts[0]);
                                const wcChainId =
                                    typeof wcProvider.chainId === "string"
                                        ? parseInt(wcProvider.chainId, 16)
                                        : Number(wcProvider.chainId);
                                if (Number.isFinite(wcChainId)) setChainId(wcChainId);
                                cleanups.push(attachWalletConnectListeners(wcProvider));
                                return;
                            }
                        }
                    } catch (err) {
                        // WC restore failed — fall through to the
                        // injected-wallet path so we don't block on a
                        // dead WC session.
                        console.warn("WalletConnect restore failed:", err?.message || err);
                    }
                }

                // Injected wallet (MetaMask / Coinbase / Rabby) silent restore.
                if (!window.ethereum) return;
                const provider = getBrowserProvider();
                const accounts = await provider.send("eth_accounts", []);
                if (cancelled) return;
                if (accounts && accounts.length > 0) {
                    const restoredSigner = await provider.getSigner();
                    const network = await provider.getNetwork();
                    setSigner(restoredSigner);
                    setAddress(accounts[0]);
                    setChainId(Number(network.chainId));
                }
            } catch (err) {
                if (!cancelled) setError(err.shortMessage || err.message || String(err));
            } finally {
                if (!cancelled) setIsRestoring(false);
            }
        })();

        if (window.ethereum) {
            const onAccounts = async (accounts) => {
                if (!accounts || accounts.length === 0) {
                    setAddress(null);
                    setSigner(null);
                    return;
                }
                if (readDisconnectedFlag()) return;
                try {
                    const provider = getBrowserProvider();
                    const newSigner = await provider.getSigner();
                    setSigner(newSigner);
                    setAddress(accounts[0]);
                } catch (err) {
                    setError(err.shortMessage || err.message || String(err));
                }
            };
            const onChain = (hex) => setChainId(parseInt(hex, 16));
            window.ethereum.on?.("accountsChanged", onAccounts);
            window.ethereum.on?.("chainChanged", onChain);
            cleanups.push(() => {
                window.ethereum.removeListener?.("accountsChanged", onAccounts);
                window.ethereum.removeListener?.("chainChanged", onChain);
            });
        }

        return () => {
            cancelled = true;
            for (const fn of cleanups) {
                try {
                    fn();
                } catch {
                    /* ignore */
                }
            }
        };
    }, []);

    /**
     * Wires up the standard EIP-1193 events (accounts/chain change,
     * disconnect) on a WalletConnect provider so updates from the user's
     * mobile wallet flow back into the React state.
     *
     * Returns an unsubscribe function the effect can call on teardown.
     */
    function attachWalletConnectListeners(wcProvider) {
        const onAccounts = (accounts) => {
            if (!accounts || accounts.length === 0) {
                setAddress(null);
                setSigner(null);
                return;
            }
            setAddress(accounts[0]);
        };
        const onChain = (raw) => {
            const id = typeof raw === "string" ? parseInt(raw, 16) : Number(raw);
            if (Number.isFinite(id)) setChainId(id);
        };
        const onDisconnect = () => {
            setSigner(null);
            setAddress(null);
            setEnsName(null);
            setWalletKind(null);
            writeWalletKind(null);
        };
        wcProvider.on?.("accountsChanged", onAccounts);
        wcProvider.on?.("chainChanged", onChain);
        wcProvider.on?.("disconnect", onDisconnect);
        return () => {
            wcProvider.removeListener?.("accountsChanged", onAccounts);
            wcProvider.removeListener?.("chainChanged", onChain);
            wcProvider.removeListener?.("disconnect", onDisconnect);
        };
    }

    /**
     * Triggers the wallet connection flow against `eip1193Provider` (an
     * EIP-6963 announcement) when supplied, or against `window.ethereum`
     * for legacy compatibility. Verifies the chain matches the
     * configured network and adds it when the wallet doesn't know it.
     */
    async function connect(eip1193Provider, kindHint = null) {
        try {
            setError(null);
            writeDisconnectedFlag(false);
            const activeProvider = eip1193Provider || resolveProvider(null);
            setActiveProvider(activeProvider);
            const newSigner = await connectWallet();
            // Fast-path the address out of the EIP-1193 provider when
            // available — `signer.getAddress()` on a slow mobile wallet
            // would otherwise round-trip via `eth_accounts`.
            let addr;
            if (Array.isArray(activeProvider?.accounts) && activeProvider.accounts.length > 0) {
                addr = activeProvider.accounts[0];
            } else {
                addr = await newSigner.getAddress();
            }
            const provider = getBrowserProvider();
            // Prefer the EIP-1193 provider's own `chainId` (synchronous
            // read on WalletConnect, set during pairing) over a JSON-RPC
            // round-trip — `getNetwork()` can stall on mobile wallets
            // that are slow to respond to `eth_chainId`.
            let resolvedChainId = activeProvider?.chainId;
            if (typeof resolvedChainId === "string") {
                resolvedChainId = parseInt(resolvedChainId, 16);
            }
            if (!Number.isFinite(resolvedChainId)) {
                try {
                    const network = await provider.getNetwork();
                    resolvedChainId = Number(network.chainId);
                } catch {
                    resolvedChainId = null;
                }
            }
            setSigner(newSigner);
            setAddress(addr);
            if (resolvedChainId != null) setChainId(resolvedChainId);
            const kind = kindHint || detectKindFromProvider(activeProvider) || "other";
            setWalletKind(kind);
            writeWalletKind(kind);
            if (kind === "walletconnect" && activeProvider?.on) {
                attachWalletConnectListeners(activeProvider);
            }
            return { ok: true };
        } catch (err) {
            const message = err.shortMessage || err.message || String(err);
            setError(message);
            return { ok: false, error: message };
        }
    }

    /**
     * Clears any sticky error state. Used by the wallet selection modal
     * when it closes so a previous rejection does not leak into the next
     * connection attempt.
     */
    function clearError() {
        setError(null);
    }

    /** Asks the wallet to switch to the configured network. */
    async function switchNetwork() {
        try {
            setError(null);
            await ensureChain();
        } catch (err) {
            setError(err.shortMessage || err.message || String(err));
        }
    }

    function disconnect() {
        writeDisconnectedFlag(true);
        writeWalletKind(null);
        setSigner(null);
        setAddress(null);
        setEnsName(null);
        setWalletKind(null);
        // Tear down any live WalletConnect session so reload doesn't
        // silently re-restore it and the next "Wallet Connect" click
        // shows a fresh QR instead of resuming the abandoned pairing.
        resetWalletConnectProvider().catch(() => {
            /* ignore — best-effort cleanup */
        });
    }

    /**
     * Whenever the connected address changes, kick off a best-effort ENS
     * lookup against mainnet. Result is cached in `services/ens.js` so
     * later page navigations don't re-hit the network.
     */
    useEffect(() => {
        if (!address) {
            setEnsName(null);
            return;
        }
        let cancelled = false;
        lookupEnsName(address).then((name) => {
            if (!cancelled) setEnsName(name);
        });
        return () => {
            cancelled = true;
        };
    }, [address]);

    /**
     * Opens the wallet-connect modal and remembers the destination the user
     * was trying to reach. After a successful connect, the WalletConnect
     * component reads this destination and navigates there. Pass `null`
     * (or omit) for the plain top-bar Connect button so the default
     * /dashboard redirect kicks in.
     */
    function requestConnect(destination = null) {
        setPendingDestination(destination);
        setConnectModalOpen(true);
    }

    function closeConnectModal() {
        setConnectModalOpen(false);
    }

    /** Returns and clears the pending destination in a single call. */
    function consumePendingDestination() {
        const dest = pendingDestination;
        setPendingDestination(null);
        return dest;
    }

    const wrongChain = address !== null && chainId !== null && chainId !== config.network.chainId;

    const value = useMemo(
        () => ({
            address,
            ensName,
            signer,
            chainId,
            error,
            wrongChain,
            isRestoring,
            walletKind,
            connect,
            switchNetwork,
            disconnect,
            clearError,
            connectModalOpen,
            pendingDestination,
            requestConnect,
            closeConnectModal,
            consumePendingDestination
        }),
        [address, ensName, signer, chainId, error, wrongChain, isRestoring, walletKind, connectModalOpen, pendingDestination]
    );

    return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}

/** Hook to access the wallet context. Throws if used outside provider. */
export function useWallet() {
    const ctx = useContext(WalletContext);
    if (!ctx) throw new Error("useWallet must be used inside WalletProvider");
    return ctx;
}
